import React, { useEffect, useState } from "react";
import { css } from "@emotion/react";
import { Table, TableBody, TableRow, Cell, TableHeader, TableHead } from "@vds/tables";
import { SmallText } from "../../components/core/utility";
import { CustomSmallText } from "./ContractDetailsData";
import { Icon } from "@vds/icons";

const CustomTableHeader = css`
  margin-bottom: 0px;
  table-layout: fixed;
  thead {
    border-bottom: none;
    margin-bottom: 0;
    th {
      padding: 0.35rem 0rem 0.35rem 0rem;
      font-size: 0.7rem;
    }
  }
  tr {
    td {
      padding: 1px 1px 1px 0px;
      font-size: 0.7rem;
      line-height: 1;
    }

    border-bottom: none !important;
    border-top: none !important;
  }

  tr.first-subscription:not(:first-child) {
    border-top: 1px solid #e5e5e5 !important;
  }

  tr.vehicle-subscription:not(.first-subscription) {
    td {
      padding-top: 0px;
      padding-bottom: 0px;
    }
  }

  tr.first-subscription td {
    padding-top: 2px;
    padding-bottom: 0px;
  }

  td .smallText {
    line-height: 0.9;
    margin: 0;
    padding: 0;
  }

  tr.vehicle-row-even {
    background-color: rgb(240, 240, 240);
  }

  tr.vehicle-row-odd {
    background-color: #ffffff;
  }
`;

export const VehiclesDisplayTable = ({ data }) => {
  const [processedData, setProcessedData] = useState([]);

  useEffect(() => {
    if (data && data.length > 0) {
      const formattedData = JSON.parse(JSON.stringify(data)).map((vehicle) => {
        vehicle.subscriptions.sort((a, b) => a.type.localeCompare(b.type));
        return vehicle;
      });
      setProcessedData(formattedData);
    } else {
      setProcessedData([]);
    }
  }, [data]);

  const getRowSpanInfo = (vehicleIndex) => {
    if (!processedData[vehicleIndex]) return { rowSpan: 0, isFirstRow: false };
    const subscriptionsCount = processedData[vehicleIndex].subscriptions.length;
    return { rowSpan: subscriptionsCount, isFirstRow: true };
  };

  const renderTableRows = () => {
    if (!processedData.length) {
      return <TableRow role="row">No Data to display</TableRow>;
    }

    const rows = [];

    processedData.forEach((vehicle, vehicleIndex) => {
      const { rowSpan, isFirstRow } = getRowSpanInfo(vehicleIndex);

      const rowColorClass = vehicleIndex % 2 === 0 ? "vehicle-row-even" : "vehicle-row-odd";

      vehicle.subscriptions.forEach((subscription, subIndex) => {
        const isFirst = subIndex === 0;

        const rowClassName = `vehicle-subscription ${isFirst ? "first-subscription" : ""} ${rowColorClass}`;

        rows.push(
          <TableRow role="row" key={`${vehicle.vin}-${subscription.subscriptionId}-${subIndex}`} className={rowClassName}>
            {isFirst && (
              <>
                <Cell role="cell" width="25%" rowSpan={rowSpan}>
                  <SmallText style={{ fontSize: "0.7rem", lineHeight: "1.2" }}>
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </SmallText>
                  {vehicle.displayVinId && <SmallText aria-label={vehicle.vin}>{vehicle.vin ? vehicle.vin : ""}</SmallText>}
                </Cell>
                <Cell role="cell" width="15%" rowSpan={rowSpan}>
                  {vehicle.wheelChair && (
                    <Icon
                      className="InternationalSymbolOfAccess"
                      aria-label="InternationalSymbolOfAccess"
                      alt="InternationalSymbolOfAccess"
                      name="international-symbol-of-access"
                      size="Large"
                      color="#000000"
                      style={{ cursor: "pointer", outline: "none", width: "1rem", blockSize: "1rem" }}
                    />
                  )}
                  {vehicle.emergency && (
                    <Icon
                      className="police-fleet"
                      aria-label="police-fleet"
                      alt="police-fleet"
                      name="police-fleet"
                      size="Large"
                      color="#000000"
                      style={{ cursor: "pointer", outline: "none", width: "1rem", blockSize: "1rem" }}
                    />
                  )}
                  {vehicle.killSwitch && (
                    <Icon
                      className="smart-switch"
                      aria-label="smart-switch"
                      alt="smart-switch"
                      name="smart-switch"
                      size="Large"
                      color="#000000"
                      style={{ cursor: "pointer", outline: "none", width: "1rem", blockSize: "1rem" }}
                    />
                  )}
                  <SmallText aria-label={vehicle.weightClass}>{vehicle.weightClass ? vehicle.weightClass : ""}</SmallText>
                </Cell>
              </>
            )}
            <Cell role="cell" width="10%">
              <SmallText aria-label={subscription.type}>{subscription.type ? subscription.type : "--"}</SmallText>
            </Cell>
            <Cell role="cell" width="15%">
              <SmallText aria-label={subscription.subType}>{subscription.subType ? subscription.subType : "--"}</SmallText>
            </Cell>
            <Cell role="cell" width="35%">
              <SmallText aria-label={subscription.subscriptionId}>
                {subscription.subscriptionName ? `${subscription.subscriptionName}(${subscription.subscriptionId})` : "--"}
              </SmallText>
            </Cell>
            <Cell role="cell" width="30%">
              <SmallText aria-label={subscription.techRefId}>{subscription.techRefId ? subscription.techRefId : "--"}</SmallText>
            </Cell>
            <Cell role="cell" width="35%">
              <SmallText aria-label={subscription.hardwareName}>{subscription.hardwareName ? subscription.hardwareName : "--"}</SmallText>
            </Cell>
            <Cell role="cell" width="20%">
              <SmallText aria-label={subscription.hardwareId}>{subscription.hardwareId ? subscription.hardwareId : "--"}</SmallText>
            </Cell>
          </TableRow>
        );
      });
    });

    return rows;
  };

  return (
    <>
      <Table role="table" tabIndex={0} topLine="none" css={CustomTableHeader}>
        <TableHead role="row" tabIndex={0}>
          <TableHeader tabIndex={0} role="columnheader" id="Vehicles/Assets" width="25%">
            <CustomSmallText tabIndex={0} bold>
              Vehicles/ Assets
            </CustomSmallText>
          </TableHeader>
          <TableHeader role="columnheader" id="traits" width="15%">
            <CustomSmallText bold>Traits</CustomSmallText>
          </TableHeader>
          <TableHeader role="columnheader" id="type" width="15%">
            <CustomSmallText bold>Category</CustomSmallText>
          </TableHeader>
          <TableHeader role="columnheader" id="subscriptionName" width="15%">
            <CustomSmallText bold>Sub-Category</CustomSmallText>
          </TableHeader>
          <TableHeader role="columnheader" id="subscriptionId" width="35%">
            <CustomSmallText bold>Subscription</CustomSmallText>
          </TableHeader>
          <TableHeader role="columnheader" id="techRefId" width="30%">
            <CustomSmallText bold>Tech Ref ID</CustomSmallText>
          </TableHeader>
          <TableHeader role="columnheader" id="hardwareName" width="35%">
            <CustomSmallText bold>Hardware Name</CustomSmallText>
          </TableHeader>
          <TableHeader role="columnheader" id="hardwareId" width="20%">
            <CustomSmallText bold>Hardware ID</CustomSmallText>
          </TableHeader>
        </TableHead>
        <TableBody role="rowgroup" tabIndex={0}>
          {renderTableRows()}
        </TableBody>
      </Table>
    </>
  );
};
